import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label } from "../roads/_roadsCommon";

/** Ch 26.4 — Reinforcement Detailing Drawings (Wave N). */
export default function RebarDrawingsPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [tab, setTab] = useState("beam");
  const [activeId, setActiveId] = useState(null);
  const [svg, setSvg] = useState("");
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/structural-re/model`);
      setModel(r.data.model);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const loadSvg = async (kind, id) => {
    try {
      const r = await api.get(`/projects/${projectId}/structural-re/details/${kind}/${id}.svg`);
      setSvg(r.data.svg);
      setActiveId(id);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const items = (model?.[tab + "s"] || []);

  return (
    <div data-testid="rebar-drawings" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Element list */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
          {["beam", "column", "slab", "footing"].map(t => (
            <button key={t} data-testid={`rebar-tab-${t}`}
                style={{ ...btnGhost, padding: "4px 8px", fontSize: 10,
                            ...(tab === t ? { background: "#F26B1F", color: "#0e0e10" } : {}) }}
                onClick={() => { setTab(t); setSvg(""); setActiveId(null); }}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>
        <div style={label}>{tab}s ({items.length})</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
          {items.map(it => (
            <button key={it.id} data-testid={`rebar-pick-${it.id}`}
                style={{ ...btnGhost, textAlign: "left", padding: "5px 8px", fontSize: 11,
                            ...(activeId === it.id ? { borderColor: "#F26B1F" } : {}) }}
                onClick={() => loadSvg(tab, it.id)}>
              {it.id} <span style={{ float: "right", fontSize: 9, color: "var(--pf-muted)" }}>
                {it.type}
              </span>
            </button>
          ))}
          {items.length === 0 && (
            <div style={{ fontSize: 10, color: "var(--pf-muted)", marginTop: 8 }}>
              No {tab}s. Create elements in the Structural Editor first.
            </div>
          )}
        </div>
      </div>

      {/* Drawing */}
      <div style={{ ...card, padding: 12, overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={label}>{activeId ? `${tab.toUpperCase()} ${activeId} — IS 456 / IS 13920` : "Select an element"}</div>
          {svg && (
            <a href={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
                download={`${tab}-${activeId}-detailing.svg`}
                style={{ ...btn, textDecoration: "none" }}>
              ↓ Download SVG
            </a>
          )}
        </div>
        {svg ? (
          <div style={{ marginTop: 12 }} dangerouslySetInnerHTML={{ __html: svg }} />
        ) : (
          <div style={{ marginTop: 24, color: "var(--pf-muted)", fontSize: 11 }}>
            Pick a {tab} from the left to render its reinforcement detailing.
            Each drawing shows longitudinal section, cross-sections, bar marks,
            anchorage Ld, and lap-length per IS 456 / IS 13920.
          </div>
        )}
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}
